import { Checkbox, Divider, TextInput } from "@hoylu/client-common";
import { clsx } from "clsx";
import React, { type SubmitEventHandler, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { Label, WorkspaceLabels } from "../../state/workspaces/types";
import { toggleIncludeHiddenWorkspaces } from "../../state/workspaces/workspaces.actions";
import { selectHiddenSectionIsVisible } from "../../state/workspaces/workspaces.selector";
import { useI18n } from "../../utils/hooks/use.i18n";
import Styles from "./EditLabelContent.module.css";
import { TileTag } from "./editLabel/TileTag";

export type EditLabelContentProps = {
  allLabels: WorkspaceLabels;
  selected: WorkspaceLabels | null | undefined;
  updateSelected: (selected: WorkspaceLabels) => void;
  canAddNewLabels?: boolean;
  enableHiddenWorkspaceSearch?: boolean;
  subscribeLabelsChanges?: boolean;
};

type Tag = {
  checked: boolean;
  name: string;
};

export const EditLabelContent: React.FC<EditLabelContentProps> = ({
  allLabels,
  selected,
  updateSelected,
  canAddNewLabels = true,
  subscribeLabelsChanges = false,
  enableHiddenWorkspaceSearch = false,
}) => {
  const dispatch = useDispatch();

  const tEditLabelDialog = useI18n("EDIT_LABEL_DIALOG.");
  const tFilterByWsLabel = useI18n("FILTER_BY_WORKSPACE_LABEL.");

  const isChecked = useSelector(selectHiddenSectionIsVisible);

  const currentWorkspaceLabels =
    selected?.map((label: Label) => ({
      checked: true,
      name: label.name,
    })) || [];

  const [labelField, setLabelField] = useState("");
  const [tags, setTags] = useState(
    currentWorkspaceLabels
      .concat(
        allLabels.map((label: Label) => ({
          checked: false,
          name: label.name,
        })) || [],
      )
      .filter(
        (tag: Tag, index, self) =>
          self.map((val: Tag) => val.name).indexOf(tag.name) === index,
      ),
  );

  if (subscribeLabelsChanges) {
    // FIXME: with investigate part, something is off with this component.
    const selectedLabelsOneDimension = currentWorkspaceLabels.map(
      (label: Label) => label.name,
    );
    const updatedTags = tags.map((label: Label) => ({
      name: label.name,
      checked: selectedLabelsOneDimension.includes(label.name),
    }));
    if (JSON.stringify(updatedTags) !== JSON.stringify(tags)) {
      // FIXME: Address this unintentional side-effect.
      setTags(updatedTags);
    }
  }

  const searchTagIndex = (str: string) => {
    return tags.findIndex(
      (tag: Tag) =>
        tag.name.trim().toLocaleLowerCase() === str.trim().toLocaleLowerCase(),
    );
  };

  const searchTag = (str: string) => {
    return tags.find(
      (tag: Tag) =>
        tag.name.trim().toLocaleLowerCase() === str.trim().toLocaleLowerCase(),
    );
  };

  const filterTags = (str: string) => {
    return tags.filter(
      (tag: Tag) =>
        tag.name
          .trim()
          .toLocaleLowerCase()
          .indexOf(str.trim().toLocaleLowerCase()) !== -1,
    );
  };

  const requestUpdateLabels = (tags: Tag[]) => {
    updateSelected(
      tags
        .filter((tag: Tag) => tag.checked)
        .map((tag: Tag) => ({ name: tag.name })),
    );
  };

  const submitAction: SubmitEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    const value = labelField.trim();
    if (value === "") return;
    const existTagIndex = searchTagIndex(value);
    let newTags = [...tags];
    if (existTagIndex !== -1) {
      newTags[existTagIndex] = {
        ...newTags[existTagIndex],
        checked: !newTags[existTagIndex].checked,
      };
    } else if (canAddNewLabels) {
      newTags = tags.concat([{ checked: true, name: labelField }]);
    }
    setTags(newTags);
    requestUpdateLabels(newTags);
    setLabelField("");
  };

  const onToggleTag = (text: string, checked: boolean) => {
    const newTags = tags.map((tag: Tag) => ({
      name: tag.name,
      checked: tag.name === text ? checked : tag.checked,
    }));
    setTags(newTags);
    requestUpdateLabels(newTags);
  };

  const showHiddenWorkspaces = () => {
    dispatch(toggleIncludeHiddenWorkspaces());
  };

  const foundTag = searchTag(labelField);
  const filteredTags = filterTags(labelField);
  const hasCheckedTag = foundTag?.checked;
  const shouldDisplayAddRemoveIcon =
    labelField.trim() !== "" && (!!foundTag || canAddNewLabels);

  const inputClasses = useMemo(() => {
    const inputClasses = [Styles.input];
    if (shouldDisplayAddRemoveIcon) {
      inputClasses.push(Styles.rightSpacing);
    }
    return inputClasses;
  }, [shouldDisplayAddRemoveIcon]);

  return (
    <form autoComplete="off" onSubmit={submitAction}>
      <div className={Styles.content} data-test-id="workspace-labels-editor">
        <TextInput
          classNames={inputClasses}
          isFocus
          label=""
          onChange={setLabelField}
          onKeyUp={(e) => {
            if (e.key === "Escape") setLabelField("");
          }}
          value={labelField}
          watermark={tEditLabelDialog("ENTER_LABEL")}
        />
        {shouldDisplayAddRemoveIcon && (
          <button
            className={clsx(
              Styles.addRemoveIcon,
              hasCheckedTag ? "hoylu-ui-icons-x" : "hoylu-ui-icons-plus",
            )}
            title={tEditLabelDialog(hasCheckedTag ? "REMOVE" : "ADD")}
            type="submit"
          />
        )}
      </div>
      <div className={Styles.labels}>
        {filteredTags.map((tag: Tag) => (
          <TileTag
            checked={tag.checked}
            key={tag.name}
            onToggle={onToggleTag}
            text={tag.name}
          />
        ))}
      </div>
      {enableHiddenWorkspaceSearch && (
        <>
          <Divider horizontal={true} width="80%" />
          <div className={Styles.hiddenWorkspaceSearch}>
            <Checkbox checked={isChecked} onChange={showHiddenWorkspaces}>
              {tFilterByWsLabel("INCLUDE_HIDDEN_WORKSPACES")}
            </Checkbox>
          </div>
        </>
      )}
    </form>
  );
};
